import React, { useEffect, useState } from 'react';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { ScrollReveal } from '@/components/ui/ScrollReveal';

interface Department {
  name: string;
  completed: number;
  total: number;
}

export const AssessmentProgressTracker = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Small delay to trigger animations after mount
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const departments: Department[] = [
    { name: 'Executive Team', completed: 8, total: 8 },
    { name: 'Marketing', completed: 12, total: 15 },
    { name: 'Operations', completed: 24, total: 24 },
    { name: 'Sales', completed: 18, total: 32 },
  ];

  const totalCompleted = departments.reduce((acc, curr) => acc + curr.completed, 0);
  const totalUsers = departments.reduce((acc, curr) => acc + curr.total, 0);
  const overallPercentage = Math.round((totalCompleted / totalUsers) * 100);

  return (
    <div className="bg-white border border-ground-deep p-8 shadow-sm">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h3 className="text-2xl font-serif text-ink mb-2">Assessment Completion</h3>
          <p className="text-sm text-ink/70">Live tracking of team diagnostic progress</p>
        </div>
        <div className="text-right">
          <span className="text-4xl font-serif text-rule-3">{overallPercentage}%</span>
          <p className="text-xs font-bold uppercase tracking-widest text-ink/70 mt-1">Overall</p>
        </div>
      </div>

      <div className="w-full h-3 bg-ground/50 rounded-full mb-10 overflow-hidden">
        <div 
          className="h-full bg-rule-3 transition-all duration-1500 ease-out rounded-full"
          style={{ width: isLoaded ? `${overallPercentage}%` : '0%' }}
        />
      </div>

      <div className="space-y-6">
        {departments.map((dept, index) => {
          const percentage = Math.round((dept.completed / dept.total) * 100);
          const isComplete = percentage === 100;
          
          return (
            <div key={dept.name} className="group">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-ink">{dept.name}</span>
                  <div className="relative group/tooltip flex items-center">
                    {isComplete ? (
                      <CheckCircle2 className="w-4 h-4 text-rule-3" />
                    ) : percentage > 50 ? (
                      <Clock className="w-4 h-4 text-amber-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-400" />
                    )}
                    
                    {/* Tooltip Content */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-ink text-white text-xs leading-relaxed text-center rounded opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 z-10 shadow-lg pointer-events-none">
                      {isComplete 
                        ? "All members completed. Ready for Discovery Day." 
                        : percentage > 50 
                        ? "Assessments in progress. The team is on track." 
                        : "Action needed. Reminders recommended to ensure timely completion."}
                      {/* Tooltip Arrow */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ink"></div>
                    </div>
                  </div>
                </div>
                <span className="text-sm font-medium text-ink/70">
                  {dept.completed} / {dept.total}
                </span>
              </div>
              <div className="w-full h-2 bg-ground/50 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-1000 ease-out rounded-full ${isComplete ? 'bg-rule-3' : 'bg-ink/60'}`}
                  style={{ 
                    width: isLoaded ? `${percentage}%` : '0%',
                    transitionDelay: `${index * 150}ms`
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-8 pt-6 border-t border-ground-deep flex justify-between items-center">
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-rule-3"></div>
            <span className="text-xs text-ink/70">Completed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-ink/60"></div>
            <span className="text-xs text-ink/70">In Progress</span>
          </div>
        </div>
        <button className="text-xs font-bold uppercase tracking-widest text-ink hover:text-rule-3 transition-colors">
          Send Reminders
        </button>
      </div>
    </div>
  );
};
